import { ReactNode } from 'react'
import { useAppSelector, selectUserOrganisation } from '@app.raytd.com/store'
import { useUpgradeModal } from '@/app/contexts/UpgradeModalContext'
import { FeatureKey, isFeatureLocked, getFeatureDisplayName } from '@/app/config/features'
import { Lock } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

interface RestrictedProps {
  to: FeatureKey
  children: ReactNode
  /**
   * 'blur' - Blurs the content and creates an overlay (Good for charts/text)
   * 'hide' - Removes content entirely
   * 'disabled' - Greys out content and adds lock icon (Good for buttons)
   */
  variant?: 'blur' | 'hide' | 'disabled'
}

/**
 * Restricted component that wraps UI elements to check feature access.
 * If the feature is locked, it renders the element in a disabled state with visual indicators.
 * 
 * @example
 * ```tsx
 * <Restricted to="export_pdf" variant="disabled">
 *   <Button>Export to PDF</Button>
 * </Restricted>
 * 
 * <Restricted to="advanced_reports" variant="blur">
 *   <Chart data={data} />
 * </Restricted>
 * ```
 */
export function Restricted({
  to,
  children,
  variant = 'disabled',
}: RestrictedProps) {
  const organisation = useAppSelector(selectUserOrganisation)
  const { triggerUpgrade } = useUpgradeModal()
  const locked = isFeatureLocked(to, organisation)

  if (!locked) {
    return <>{children}</>
  }

  const featureName = getFeatureDisplayName(to)
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    triggerUpgrade(featureName)
  }

  if (variant === 'hide') {
    return null
  }

  if (variant === 'blur') {
    return (
      <div className="relative group cursor-pointer overflow-hidden rounded-md" onClick={handleClick}>
        <div className="blur-sm select-none opacity-60 pointer-events-none grayscale transition-all duration-300">
            {children}
        </div>
        <div className="absolute inset-0 flex items-center justify-center bg-white/10 group-hover:bg-white/20 transition-colors z-10">
           <div className="bg-white/90 backdrop-blur shadow-sm border border-zinc-200 rounded-full px-4 py-1.5 flex items-center gap-2 text-sm font-medium text-zinc-800">
              <Lock className="w-3.5 h-3.5 text-indigo-500" />
              <span>Unlock {featureName}</span>
           </div>
        </div>
      </div>
    )
  }

  // Default: 'disabled' variant (Best for buttons/nav items)
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            onClick={handleClick}
            className="relative inline-flex opacity-60 grayscale cursor-not-allowed group"
          >
            {/* Render children but kill pointer events on them specifically */}
            <div className="pointer-events-none select-none flex items-center gap-2">
                {children}
                <Lock className="w-3 h-3 text-zinc-500" />
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent className="flex items-center gap-2 bg-zinc-900 text-white border-zinc-800">
            <Lock className="w-3 h-3 text-indigo-400" />
            <p>Upgrade to use {featureName}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

